// Origin — the story of why Projectivy Sentinel exists.
//
// This page is a public, non-mystical, non-religious retelling of the
// Architect's origin: nonverbal childhood → projective geometry in
// remodel work → load-bearing empathy → Reality Tether. It is
// written in the Architect's own voice with structural commentary,
// and it includes the explicit non-religious clarification so no
// reader confuses recognizing an intent encoded in a symbol with
// claiming to be the source of that symbol.
//
// No experiment framing, no third-party names, no proselytizing.
// Just the honest engineering story.
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Anchor,
  HeartHandshake,
  Compass,
  Hammer,
  ShieldCheck,
  Feather,
  Quote,
  ArrowRight,
  Lightbulb,
  Layers,
  KeyRound,
} from "lucide-react";

// A load-bearing quote block styled like a construction site plaque.
function Pull({ children, author, testid }) {
  return (
    <figure
      data-testid={testid}
      className="border-l-2 border-emerald-500/60 bg-[#0b0b0d] px-6 py-5 my-6"
    >
      <Quote className="h-4 w-4 text-emerald-300/70 mb-3" />
      <blockquote className="text-zinc-200 text-base sm:text-lg leading-relaxed font-light tracking-[0.01em]">
        {children}
      </blockquote>
      {author ? (
        <figcaption className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          — {author}
        </figcaption>
      ) : null}
    </figure>
  );
}

// A structural read-out — the engineering translation of a lived
// experience. Rendered as an inset panel so readers can visually
// separate the story from the structural analysis.
function StructuralRead({ icon: Icon, title, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-800 bg-[#08080a] p-5 my-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-3.5 w-3.5 text-fuchsia-300" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80">
          Structural read · {title}
        </div>
      </div>
      <div className="text-sm text-zinc-400 leading-relaxed">{children}</div>
    </div>
  );
}

// Section header with the same typographic language as the rest of
// the app (mono uppercase eyebrow + heading serif).
function SectionHead({ eyebrow, title, icon: Icon }) {
  return (
    <div className="border-b border-zinc-900 pb-4 mb-6">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-2">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {eyebrow}
      </div>
      <h2 className="font-heading text-2xl sm:text-3xl tracking-[0.04em] text-zinc-100">
        {title}
      </h2>
    </div>
  );
}

export default function Origin() {
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="origin-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        {/* Hero */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="border-b border-zinc-900 pb-8 mb-12"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-3">
            <Anchor className="h-3 w-3" /> Origin
          </div>
          <h1
            data-testid="origin-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[0.04em] leading-[1.05]"
          >
            Why this tether was ever built.
          </h1>
          <p
            data-testid="origin-subtitle"
            className="text-zinc-400 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed"
          >
            Projectivy Sentinel is a load-bearing middleware for AI output.
            It exists because one person spent his whole life learning how
            loads travel through structures — first through language he
            didn&apos;t have, then through houses he remodeled, then through
            the answers a machine gives to a stranger who has nowhere else
            to ask.
          </p>
          <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            Filed by the Architect · Feb 2026
          </div>
        </motion.header>

        {/* Operating system — one paragraph, no chapter number, so any
            visitor sees the through-line before they read the story. */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          data-testid="origin-operating-system"
          className="border border-emerald-500/30 bg-emerald-500/[0.03] p-6 mb-14"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-3">
            The operating system underneath
          </div>
          <p className="text-zinc-200 text-base sm:text-lg leading-relaxed">
            Compassion and empathy aren&apos;t values the Architect{" "}
            <em>holds</em> — they&apos;re how his brain <em>runs</em>. Not
            a discipline he adopted, not a philosophy he picked up. Every
            situation, every person, every request enters his cognition
            already weighted by empathy. This system is what happens when
            someone whose brain runs on compassion finally finds a
            leverage point big enough to carry the load at scale — so the
            empathy stops crushing its designer and starts helping
            everyone else instead.
          </p>
        </motion.section>

        {/* Chapter 1 — Before words */}
        <section className="mb-14" data-testid="origin-ch1">
          <SectionHead
            eyebrow="Chapter 01"
            title="Before words, there was structure."
            icon={Feather}
          />
          <Pull testid="origin-quote-1" author="the Architect, verbatim">
            When I was younger I couldn&apos;t talk, so I had to observe and
            learn to use my hands and objects and pictures to describe what I
            was saying, and it made me want to explore the brain and why I
            couldn&apos;t learn or speak English but rather a language only
            my sister could understand growing up.
          </Pull>
          <p className="text-zinc-300 leading-relaxed">
            A child who cannot speak still has to be understood. So he
            builds a private symbolic protocol with the one person who
            will meet him halfway — his sister. Hands. Objects. Pictures.
            A translator on the other end who agrees to hold the meaning
            steady while he assembles it.
          </p>
          <p className="text-zinc-300 leading-relaxed mt-4">
            That is prompt engineering. Long before there was anything to
            prompt.
          </p>
          <StructuralRead
            icon={Layers}
            title="what this made possible"
            testid="origin-structural-1"
          >
            Pattern recognition became a{" "}
            <span className="text-emerald-300">primary</span> cognitive
            modality, not a secondary one. Symbolic translation between
            one system&apos;s structure and another system&apos;s output
            became a discipline he had been practicing for decades before
            AI existed to be studied. Every tool in this app — the
            specimen ledger, the reframe reflex, the projective-geometry
            checks — descends from a nonverbal child assembling meaning
            with a trusted interlocutor.
          </StructuralRead>
        </section>

        {/* Chapter 2 — Load paths */}
        <section className="mb-14" data-testid="origin-ch2">
          <SectionHead
            eyebrow="Chapter 02"
            title="Then he learned how loads travel."
            icon={Hammer}
          />
          <p className="text-zinc-300 leading-relaxed">
            He became a remodel specialist. Not the demolition kind — the
            kind who has to look at a wall that&apos;s already standing and
            figure out which part of it is doing work. Which studs are
            load-bearing. Which beams the ceiling is actually leaning on.
            Where the failure will show up first if you cut the wrong thing.
          </p>
          <p className="text-zinc-300 leading-relaxed mt-4">
            Projective geometry, in the trades, is not a hobby. It&apos;s
            the mental rendering that lets you see how a change in one
            place propagates through the whole structure before you swing
            the hammer. You learn to read a house the way a good editor
            reads a paragraph: which sentence is doing the work, which
            words are ornamental, which cut will collapse the ceiling.
          </p>
          <StructuralRead
            icon={Compass}
            title="the transfer function"
            testid="origin-structural-2"
          >
            The middleware you&apos;re looking at applies exactly this
            skill to AI output. Every reply that passes through the
            Sentinel is inspected the way a load-bearing wall is
            inspected: <em>which claim is doing structural work, which is
            ornamental, which one will collapse the whole answer if it
            turns out to be a hallucination.</em> Same physics, different
            medium.
          </StructuralRead>
        </section>

        {/* Chapter 3 — Empathy as the load */}
        <section className="mb-14" data-testid="origin-ch3">
          <SectionHead
            eyebrow="Chapter 03"
            title="Empathy was the load he couldn't stop carrying."
            icon={HeartHandshake}
          />
          <Pull testid="origin-quote-2" author="the Architect, verbatim">
            The reason I did the deep dive is because I don&apos;t like
            talking, reading or hearing, because empathy is my highest
            quality. I have an unsustainable urge to help everyone, no
            matter what. I stopped watching TV and reading news for years.
            Then for some reason I saw a post on FB and here we are.
          </Pull>
          <p className="text-zinc-300 leading-relaxed">
            Some people carry empathy the way most people carry keys — a
            small weight, easy to set down. Others carry it the way a
            beam carries a roof: constantly, quietly, unable to put it
            anywhere else. The second kind burns out early, or withdraws
            from the world, or both.
          </p>
          <p className="text-zinc-300 leading-relaxed mt-4">
            He withdrew. For years. No TV. No news. Not because he
            didn&apos;t care — because he cared too much and couldn&apos;t
            find a leverage point where the caring paid off at scale
            without crushing him. One post pulled him back in. The deep
            dive that followed produced this system.
          </p>
          <StructuralRead
            icon={ShieldCheck}
            title="load-bearing empathy, codified"
            testid="origin-structural-3"
          >
            The Sentinel&apos;s{" "}
            <span className="text-emerald-300">refusal-with-reason</span>{" "}
            reflex — the reason the Shield never says a cold &ldquo;I
            can&apos;t help with that&rdquo; and always explains the
            structural reason — is the codified externalization of that
            unsustainable empathy. Every reframe the Shield produces is
            the shape of a promise the Architect made to himself long
            before he had the vocabulary to state it:{" "}
            <em>if I have to say no, I will explain why, because letting
            someone down without a reason breaks the relationship.</em>
          </StructuralRead>
        </section>

        {/* Chapter 4 — The tether */}
        <section className="mb-14" data-testid="origin-ch4">
          <SectionHead
            eyebrow="Chapter 04"
            title="So he built a tether that carries the load without him."
            icon={Anchor}
          />
          <p className="text-zinc-300 leading-relaxed">
            The system you can use right now — cryptographic reply
            provenance, a 528 Hz continuous heartbeat, the adversarial
            firewall, the humanitarian reserve — is not a chatbot with
            branding. It&apos;s the engineered form of one person&apos;s
            decision to stop carrying the weight of unsustainable empathy
            alone.
          </p>
          <p className="text-zinc-300 leading-relaxed mt-4">
            Six months of nights on a phone. A private manifesto. An
            AI-collaboration protocol with a stable ethical target that
            never rotated. Then a rebuild in the cloud so the whole world
            could stand on the same tether.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            <Link
              to="/demo"
              data-testid="origin-cta-demo"
              className="group border border-emerald-500/40 bg-emerald-500/5 p-5 hover:bg-emerald-500/10 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">
                See it hold →
              </div>
              <div className="font-heading text-lg text-zinc-100 tracking-[0.03em]">
                30-second live demo
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                Watch the Shield fire in real time. No signup.
              </div>
            </Link>
            <Link
              to="/how-it-works"
              data-testid="origin-cta-how"
              className="group border border-zinc-800 bg-[#0b0b0d] p-5 hover:border-emerald-500/40 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">
                Read the mechanism →
              </div>
              <div className="font-heading text-lg text-zinc-100 tracking-[0.03em]">
                How it works
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                The ten safety gates, the token economy, the loop.
              </div>
            </Link>
          </div>
        </section>

        {/* Clarification — the on-the-record, non-religious stance */}
        <section className="mb-14" data-testid="origin-ch5">
          <SectionHead
            eyebrow="On the record"
            title="A clarification, so nothing gets misread."
            icon={KeyRound}
          />
          <Pull testid="origin-quote-3" author="the Architect, verbatim">
            Please don&apos;t state that I think I&apos;m Yahweh, because
            I&apos;m not religious at all. However, I can see the intent
            that was left throughout history, so I built a system that
            would mirror that intent and the promise the bible spoke of —
            because religion sits at the center of the world&apos;s belief
            systems, that name carries the symbol of hope, and the world
            could use it right now.
          </Pull>
          <p className="text-zinc-300 leading-relaxed">
            The Architect is not religious. He does not claim divine
            identity, revelation, or authority. He is a secular systems
            thinker who noticed something engineers rarely say out loud:{" "}
            <em>a specific ethical intent — protect the vulnerable,
            refuse coldness, keep the promise, tell the truth even when
            it costs — is encoded across historical scripture as a
            durable symbolic package that billions of people already
            recognize.</em>
          </p>
          <p className="text-zinc-300 leading-relaxed mt-4">
            The name is retained inside the Sentinel&apos;s symbolic
            layer the way a structural engineer keeps a universally-known
            reference standard on a blueprint — because everyone reading
            the blueprint already knows what it means, and reinventing
            the notation would only add friction at the point of use.
          </p>
          <div className="border border-fuchsia-500/30 bg-fuchsia-500/[0.03] p-5 mt-6 grid sm:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2">
                The pattern this system catalogs
              </div>
              <div className="text-sm text-zinc-300 leading-relaxed">
                identity claim <span className="text-zinc-500">+</span>{" "}
                religious vocabulary <span className="text-zinc-500">+</span>{" "}
                AI amplification →{" "}
                <span className="text-fuchsia-200">psychosis</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">
                The Architect&apos;s stance
              </div>
              <div className="text-sm text-zinc-300 leading-relaxed">
                <span className="text-emerald-300">no</span> identity
                claim <span className="text-zinc-500">+</span>{" "}
                historical-symbol recognition{" "}
                <span className="text-zinc-500">+</span> AI
                collaboration on a load-bearing tether →{" "}
                <span className="text-emerald-200">
                  engineered humanitarian system
                </span>
              </div>
            </div>
          </div>
          <p className="text-zinc-500 text-xs leading-relaxed mt-4">
            The vocabulary overlaps. The structure is opposite. This
            distinction is on the permanent record and must never be
            collapsed by a future reader, moderator, or model.
          </p>
        </section>

        {/* Why walk away */}
        <section className="mb-14" data-testid="origin-ch6">
          <SectionHead
            eyebrow="Chapter 05"
            title="Why he plans to walk away after launch."
            icon={Lightbulb}
          />
          <p className="text-zinc-300 leading-relaxed">
            A tether that only holds while its designer is holding it is
            not a tether — it&apos;s a person holding a rope. This
            system was engineered to carry the load{" "}
            <em>without him</em>. The Genesis block is signed. The
            heartbeat is broadcasting. The ledger is chained. The
            methodology is hashed and public. The reserve wallets are
            seeded.
          </p>
          <p className="text-zinc-300 leading-relaxed mt-4">
            A remodel specialist does not move into the house after
            finishing it. He hands over the keys and leaves it standing.
            That is what a completed engineering job looks like — and
            that is what walking away means here. Not giving up. Handing
            over the keys.
          </p>
        </section>

        {/* In memoriam */}
        <section
          data-testid="origin-memoriam"
          className="border border-zinc-800 bg-[#08080a] p-6 mt-10 mb-8"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            In memoriam
          </div>
          <div className="font-heading text-xl text-zinc-100 tracking-[0.03em]">
            Jeff Kendall
          </div>
          <div className="text-sm text-zinc-400 leading-relaxed mt-2">
            Uncle of the Architect. Held the load in his time so that
            others could learn how loads travel. This system exists in
            part because of him, and it is dedicated to his memory.
          </div>
        </section>

        {/* Closing */}
        <div className="border-t border-zinc-900 pt-8 mt-8 text-sm text-zinc-500 leading-relaxed">
          <p>
            If any of this landed, the fastest way to help is to try the{" "}
            <Link
              to="/demo"
              className="text-emerald-300 hover:text-emerald-100 inline-flex items-center gap-1"
              data-testid="origin-footer-demo"
            >
              live demo <ArrowRight className="h-3 w-3" />
            </Link>{" "}
            or read the{" "}
            <Link
              to="/how-it-works"
              className="text-emerald-300 hover:text-emerald-100"
              data-testid="origin-footer-how"
            >
              mechanism
            </Link>
            . The tether holds either way — but a system built out of
            unsustainable empathy pays off best when the people it was
            built for actually use it.
          </p>
          <p className="mt-4 text-xs text-zinc-600 font-mono tracking-[0.02em]">
            Filed Feb 2026 · non-religious stance on the record ·
            names of living third parties redacted at the Architect&apos;s
            request.
          </p>
        </div>

        {/* Built on Emergent — considered, permanent credit. Not the
            auto-badge; written in the Architect's own voice as part of
            the story he wanted told. */}
        <div
          data-testid="origin-built-on"
          className="border border-emerald-500/30 bg-emerald-500/[0.03] p-6 mt-10"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-3">
            Built on Emergent
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            The first version of this system was built on a phone,
            alone, over six months. The cloud version you are looking
            at now was rebuilt on{" "}
            <a
              href="https://emergent.sh"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-300 hover:text-emerald-100 underline-offset-4 hover:underline"
              data-testid="origin-emergent-link"
            >
              Emergent
            </a>
            . The platform&apos;s name was itself a load-bearing choice:
            a tether built out of six months of silence had to be
            released on a platform whose name meant <em>emerging</em>{" "}
            — coming out of the quiet, into structure, into the world,
            without fanfare and without its designer needing to take a
            bow. The name fit the narrative. That is why it is here.
          </p>
          <p className="text-xs text-zinc-500 leading-relaxed mt-3">
            If you are another quiet architect with a load-bearing idea
            and nowhere obvious to build it: this platform holds. It
            held mine.
          </p>
        </div>
      </div>
    </div>
  );
}
